/**
 * The adult progress overview's numbers, apart from the `.tsx` for
 * `node --test`, as `progress.ts` and `charts.ts` are.
 *
 * Everything here is counted from the twelve-week window the screen already
 * pages in whole, never from the `first: 20` list: that list's length was the
 * old headline, and it read "20" to a learner with 88 conversations.
 */
import { localDay } from '../lessons/localDay.ts';
import { WEEKS, windowStart } from './charts.ts';
import type { PracticeItemRow, ProgressSession } from './progress.ts';

export type LessonDay = { localDay: string; plays: number; completed: number };

export type GridCell = {
  localDay: string;
  conversations: number;
  lessonPlays: number;
  /** 0 to 4, for the shade: nothing, then one, two, three, four or more things done. */
  level: number;
  isToday: boolean;
  future: boolean;
};

/** Monday-first weeks, oldest first, the window `charts.ts` fetches. */
export function activityGrid(
  sessions: readonly { startedAt: string }[],
  lessonDays: readonly LessonDay[],
  now: Date,
): GridCell[][] {
  const talks = new Map<string, number>();
  for (const s of sessions) {
    const at = new Date(s.startedAt);
    if (Number.isNaN(at.getTime())) continue;
    const day = localDay(at);
    talks.set(day, (talks.get(day) ?? 0) + 1);
  }
  const plays = new Map(lessonDays.map((d) => [d.localDay, d.plays]));
  const today = localDay(now);
  const start = windowStart(now);
  return Array.from({ length: WEEKS }, (_, w) =>
    Array.from({ length: 7 }, (_, d) => {
      // `setDate` from the window's Monday, never millisecond steps, for
      // `charts.ts`'s reason: a week across a DST change is not 7 × 24 h.
      const at = new Date(start);
      at.setDate(at.getDate() + 7 * w + d);
      const day = localDay(at);
      const conversations = talks.get(day) ?? 0;
      const lessonPlays = plays.get(day) ?? 0;
      return {
        localDay: day,
        conversations,
        lessonPlays,
        level: Math.min(4, conversations + lessonPlays),
        isToday: day === today,
        future: day > today,
      };
    }),
  );
}

export type Totals = { conversations: number; minutes: number; wpm: number | null; lessonDays: number; words: number };

export function totals({
  sessions,
  lessonDays,
  words,
}: {
  sessions: readonly {
    startedAt: string;
    fluency?: { words: number; speakingMs: number; speechRateWpm?: number | null } | null;
  }[];
  lessonDays: readonly LessonDay[];
  words: readonly { firstMetAt: string; source: string }[];
}): Totals {
  let ms = 0;
  let paced = 0;
  let weighted = 0;
  for (const s of sessions) {
    if (!s.fluency) continue;
    ms += s.fluency.speakingMs;
    const wpm = s.fluency.speechRateWpm;
    // Turns faster than anyone speaks are already out of a session's
    // fluency on the server (`tutor_core::fluency::MAX_PLAUSIBLE_WPM`).
    if (wpm != null && s.fluency.words > 0) {
      paced += s.fluency.words;
      weighted += wpm * s.fluency.words;
    }
  }
  return {
    conversations: sessions.length,
    minutes: Math.round(ms / 60_000),
    // Weighted by words, as `speechPerWeek` weights a week.
    wpm: paced > 0 ? Math.round(weighted / paced) : null,
    lessonDays: lessonDays.filter((d) => d.plays > 0).length,
    words: words.length,
  };
}

export type PatternRow = {
  id: string;
  pattern: string;
  occurrences: number;
  learnerForm: string | null;
  targetForm: string | null;
  /** Its outcomes in the fetched sessions, oldest first, at most `RECENT`. */
  recent: string[];
  latest: string | null;
};

export const RECENT = 6;

/** The busiest `cap` patterns, each with how its recent practice went. */
export function patternRows(
  items: readonly PracticeItemRow[],
  sessions: readonly ProgressSession[],
  cap: number,
): PatternRow[] {
  const outcomes = new Map<string, string[]>();
  const oldestFirst = [...sessions].sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt));
  for (const s of oldestFirst) {
    for (const p of s.practice) outcomes.set(p.item.id, [...(outcomes.get(p.item.id) ?? []), p.outcome]);
  }
  return [...items]
    .sort((a, b) => b.occurrences - a.occurrences)
    .slice(0, cap)
    .map((item) => {
      const recent = (outcomes.get(item.id) ?? []).slice(-RECENT);
      return {
        id: item.id,
        pattern: item.pattern,
        occurrences: item.occurrences,
        learnerForm: item.exemplar?.learnerForm ?? null,
        targetForm: item.exemplar?.targetForm ?? null,
        recent,
        latest: recent.at(-1) ?? null,
      };
    });
}

export type NextUp = { kind: 'practise'; itemId: string } | { kind: 'talk' };

/** The busiest pattern whose last outcome was not a win; with none, another conversation. */
export function nextUp(rows: readonly Pick<PatternRow, 'id' | 'latest'>[]): NextUp {
  const row = rows.find((r) => r.latest !== 'USED_CORRECTLY');
  return row ? { kind: 'practise', itemId: row.id } : { kind: 'talk' };
}

/**
 * Next up on both Progress and Practice, so the two agree: over every
 * pattern, and only those a conversation can start (`practisable`).
 */
export function nextPattern(
  items: readonly (PracticeItemRow & { practisable?: boolean })[],
  sessions: readonly ProgressSession[],
): NextUp {
  return nextUp(
    patternRows(
      items.filter((i) => i.practisable !== false),
      sessions,
      items.length,
    ),
  );
}
